import { IChampionRoleCounts, IChampionSeasonStats } from "../../types/types";
import type { PlayerRole } from "../leaderboard/roles";
import type { SortDirection } from "../seasonstats/table";

export type ChampionStatsSortKey =
  | "champion"
  | "picks"
  | "bans"
  | "wins"
  | "losses"
  | "winRate"
  | "pickPct"
  | "banPct"
  | "presence"
  | "kda"
  | "csPerMin"
  | "goldPerMin"
  | "dpm"
  | "gamesCounted";

/** Roles a champion can be counted in, in lane order. */
export const CHAMPION_ROLES = ["Top", "Jungle", "Mid", "ADC", "Support"] as const;

export type ChampionRole = (typeof CHAMPION_ROLES)[number];

const ASC_FIRST_SORT_KEYS = new Set<ChampionStatsSortKey>(["champion"]);

const DRAFT_SORT_KEYS = new Set<ChampionStatsSortKey>([
  "picks",
  "bans",
  "wins",
  "losses",
  "winRate",
  "pickPct",
  "banPct",
  "presence",
]);

export function defaultChampionStatsSortDirection(key: ChampionStatsSortKey): SortDirection {
  return ASC_FIRST_SORT_KEYS.has(key) ? "asc" : "desc";
}

/** True for columns that only exist when a champion sheet has been imported. */
export function isDraftSortKey(key: ChampionStatsSortKey): boolean {
  return DRAFT_SORT_KEYS.has(key);
}

export function roleGames(roles: IChampionRoleCounts | undefined, role: ChampionRole): number {
  if (!roles) return 0;
  switch (role) {
    case "Top":
      return roles.top;
    case "Jungle":
      return roles.jungle;
    case "Mid":
      return roles.mid;
    case "ADC":
      return roles.adc;
    case "Support":
      return roles.support;
    default:
      return 0;
  }
}

export interface ChampionRoleShare {
  role: PlayerRole;
  games: number;
}

/**
 * Roles a champion actually appeared in, most played first. Sheet-only seasons
 * have no role data at all and return an empty list.
 */
export function championRoleShares(roles: IChampionRoleCounts | undefined): ChampionRoleShare[] {
  if (!roles) return [];

  return CHAMPION_ROLES.map((role) => ({ role: role as PlayerRole, games: roleGames(roles, role) }))
    .filter((entry) => entry.games > 0)
    .sort((a, b) => b.games - a.games);
}

export function formatPercent(value: number | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

export function formatCount(value: number | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return String(value);
}

/**
 * Draft columns are blank for seasons with no champion sheet import, so the
 * table hides them rather than showing a wall of dashes.
 */
export function hasChampionDraftData(rows: IChampionSeasonStats[]): boolean {
  return rows.some((row) => row.gamesTotal > 0 || row.picks > 0 || row.bans > 0);
}

export function hasChampionRoleData(rows: IChampionSeasonStats[]): boolean {
  return rows.some((row) => championRoleShares(row.roles).length > 0);
}
